import { LitElement, html } from "lit";
import { customElement, state, query } from "lit/decorators.js";
import { provide } from "@lit/context";
import { APP_CONFIG } from "../config/attributes";
import { DATASET_COLORS } from "../constants";
import { themeStyles } from "../styles/themes";
import { appStyles } from "../styles/app.styles";
import { themeContext, configContext, lockedContext } from "../contexts";
import { PersistenceController } from "../controllers/persistence-controller";
import { ToastController } from "../controllers/toast-controller";
import { decodeRatings } from "../utils/serialization";
import type { Theme, RatingSet } from "../models";

import "./bab-attribute-rater";
import "./bab-spider-chart";
import "./bab-toast-manager";
import "./bab-import-modal";
import "./bab-app-header";
import "./bab-subject-tabs";
import "./bab-readonly-banner";
import "./bab-name-editor";
import "./bab-share-row";

/**
 * Top-level application shell.
 */
@customElement("bab-chart-app")
export class BabChartApp extends LitElement {
  static styles = [themeStyles, appStyles];

  @query("bab-spider-chart")
  private _chartComp!: import("./bab-spider-chart").BabSpiderChart;

  @provide({ context: configContext })
  config = APP_CONFIG;

  @state()
  @provide({ context: themeContext })
  theme: Theme = "system";

  @state()
  @provide({ context: lockedContext })
  locked = false;

  @state() private _activeSetIndex = 0;
  @state() private _clearPending = false;
  @state() private _showImportModal = false;
  
  private _persistence = new PersistenceController(this, APP_CONFIG);
  private _toasts = new ToastController(this);
  private _clearTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    super();
    this.locked = Boolean(window.location.hash);
    this.theme = this._persistence.state.theme || "system";
  }

  update(changedProperties: Map<string | number | symbol, unknown>) {
    super.update(changedProperties);
    // Sync local state if persistence state changed (e.g. via hashchange)
    if (this._persistence.state.theme && this._persistence.state.theme !== this.theme) {
      this.theme = this._persistence.state.theme;
      this._updateThemeAttribute();
    }
    // Also update locked state if we just loaded from hash and weren't locked
    if (window.location.hash && !this.locked && !this._persistence.state.datasets.every(d => Object.values(d.ratings).every(r => r === null))) {
       // This logic is a bit tricky, but generally if hash exists and we are booting or hash changed,
       // we might want to be locked. However, the user can unlock.
       // For now, let's just make sure the theme is synced. 
       // The 'locked' state is more of a UI preference that defaults to true on hash load.
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this._updateThemeAttribute();
    document.title = APP_CONFIG.title ?? "BAB Chart";
  }

  private _updateThemeAttribute() {
    this.setAttribute("data-theme", this.theme);
  }

  private _setTheme(theme: Theme) {
    this.theme = theme;
    this._persistence.update({ ...this._persistence.state, theme });
    this._updateThemeAttribute();
  }

  private _onRatingChanged(
    e: CustomEvent<{ attributeId: string; rating: number }>,
  ) {
    const datasets = [...this._persistence.state.datasets];
    datasets[this._activeSetIndex] = {
      ...datasets[this._activeSetIndex],
      ratings: {
        ...datasets[this._activeSetIndex].ratings,
        [e.detail.attributeId]: e.detail.rating,
      },
    };
    this._persistence.update({ ...this._persistence.state, datasets });
  }

  private _onNameChanged(e: CustomEvent<{ name: string }>) {
    const datasets = [...this._persistence.state.datasets];
    datasets[this._activeSetIndex] = {
      ...datasets[this._activeSetIndex],
      name: e.detail.name,
    };
    this._persistence.update({ ...this._persistence.state, datasets });
  }

  private _onColorChanged(e: CustomEvent<{ index: number; color: string }>) {
    const datasets = [...this._persistence.state.datasets];
    datasets[e.detail.index] = {
      ...datasets[e.detail.index],
      color: e.detail.color,
    };
    this._persistence.update({ ...this._persistence.state, datasets });
  }

  private _copyLink() {
    navigator.clipboard.writeText(window.location.href).then(() => {
      this._toasts.show("Link copied to clipboard!");
    });
  }

  private _clear() {
    if (!this._clearPending) {
      this._clearPending = true;
      this._clearTimer = setTimeout(() => {
        this._clearPending = false;
      }, 3000);
      return;
    }
    clearTimeout(this._clearTimer!);
    this._clearPending = false;
    
    const defaultRatings: Record<string, number | null> = {};
    for (const attr of APP_CONFIG.attributes) {
      defaultRatings[attr.id] = null;
    }
    
    this._persistence.update({
      ...this._persistence.state,
      datasets: [{ ratings: defaultRatings, color: DATASET_COLORS[0] }],
    });
    this._activeSetIndex = 0;
    this.locked = false;
    this._toasts.show("Chart cleared", "info");
  }

  private _addDataset() {
    this.locked = false;
    const defaultRatings: Record<string, number | null> = {};
    for (const attr of APP_CONFIG.attributes) {
      defaultRatings[attr.id] = null;
    }
    const color = DATASET_COLORS[this._persistence.state.datasets.length % DATASET_COLORS.length];
    const newSet: RatingSet = { 
      ratings: defaultRatings, 
      name: `${this._persistence.state.datasets.length + 1}`,
      color 
    };
    this._persistence.update({
      ...this._persistence.state,
      datasets: [...this._persistence.state.datasets, newSet],
    });
    this._activeSetIndex = this._persistence.state.datasets.length - 1;
    this._toasts.show("Added new comparison");
  }

  private _duplicateDataset(index: number) {
    if (this.locked) return;
    
    const source = this._persistence.state.datasets[index];
    const color = DATASET_COLORS[this._persistence.state.datasets.length % DATASET_COLORS.length];
    const newSet: RatingSet = {
      ratings: { ...source.ratings },
      name: `${source.name || (index + 1)} (Copy)`,
      color
    };
    
    const datasets = [...this._persistence.state.datasets];
    datasets.splice(index + 1, 0, newSet);
    
    this._persistence.update({ ...this._persistence.state, datasets });
    this._activeSetIndex = index + 1;
    this._toasts.show("Dataset duplicated");
  }

  private _removeDataset(index: number) {
    if (this.locked || this._persistence.state.datasets.length <= 1) return;
    
    const oldActiveIndex = this._activeSetIndex;
    const datasets = this._persistence.state.datasets.filter((_, i) => i !== index);
    this._persistence.update({ ...this._persistence.state, datasets });
    
    if (index < oldActiveIndex) {
      this._activeSetIndex = oldActiveIndex - 1;
    } else if (oldActiveIndex >= datasets.length) {
      this._activeSetIndex = datasets.length - 1;
    }
    
    this._toasts.show("Dataset removed", "info");
  }

  private _importDataset(url: string) {
    try {
      const hash = url.includes("#") ? url.slice(url.indexOf("#")) : `#${url}`;
      const state = decodeRatings(hash, APP_CONFIG);
      
      if (state.datasets.length > 0) {
        this.locked = false;
        const newSets = [...this._persistence.state.datasets, ...state.datasets];

        newSets.forEach((set, i) => {
            if (!set.color) set.color = DATASET_COLORS[i % DATASET_COLORS.length];
        });
        
        this._persistence.update({ ...this._persistence.state, datasets: newSets });
        this._activeSetIndex = this._persistence.state.datasets.length - 1;
        this._toasts.show(`Imported ${state.datasets.length} dataset(s)`);
        this._showImportModal = false;
      } else {
        this._toasts.show("Invalid URL or hash", "warn");
      }
    } catch {
      this._toasts.show("Failed to import", "warn");
    }
  }

  private _downloadImage(format: "png" | "svg" = "png") {
    const dataUrl = format === "png"
      ? this._chartComp.getImageDataUrl()
      : this._chartComp.getSvgDataUrl();
    const link = document.createElement("a");
    const name = this._persistence.state.datasets[this._activeSetIndex]?.name?.trim() || `${APP_CONFIG.attributeTerm || "skill"}-chart`;
    link.download = `${name.replace(/\s+/g, "-").toLowerCase()}.${format}`;
    link.href = dataUrl;
    link.click();
    this._toasts.show(`${format.toUpperCase()} saved!`);
  }

  render() {
    const activeSet = this._persistence.state.datasets[this._activeSetIndex];

    return html`
      <div class="content">
        <bab-app-header 
          .theme="${this.theme}"
          @theme-changed="${(e: CustomEvent<{theme: Theme}>) => this._setTheme(e.detail.theme)}"
        ></bab-app-header>

        <div class="banner-slot">
          <bab-readonly-banner .active="${this.locked}"></bab-readonly-banner>
        </div>

        <bab-name-editor
          .name="${activeSet?.name ?? ""}"
          .locked="${this.locked}"
          @name-changed="${this._onNameChanged}"
          @lock-changed="${(e: CustomEvent<{locked: boolean}>) => this.locked = e.detail.locked}"
        ></bab-name-editor>

        <bab-subject-tabs
          .datasets="${this._persistence.state.datasets}"
          .activeIndex="${this._activeSetIndex}"
          .locked="${this.locked}"
          @tab-changed="${(e: CustomEvent<{index: number}>) => this._activeSetIndex = e.detail.index}"
          @add-dataset="${this._addDataset}"
          @duplicate-dataset="${(e: CustomEvent<{index: number}>) => this._duplicateDataset(e.detail.index)}"
          @remove-dataset="${(e: CustomEvent<{index: number}>) => this._removeDataset(e.detail.index)}"
          @color-changed="${this._onColorChanged}"
          @open-import="${() => this._showImportModal = true}"
        ></bab-subject-tabs>

        <div class="layout">
          <div id="raters-panel" class="raters-panel ${this.locked ? "raters-panel--locked" : ""}" role="tabpanel">
            <div class="raters-header">
              <span class="raters-title">
                ${(APP_CONFIG.attributeTermPlural || "Skills").charAt(0).toUpperCase() + (APP_CONFIG.attributeTermPlural || "Skills").slice(1)}
                ${activeSet?.name ? `: ${activeSet.name}` : ""}
              </span>
              <span class="completion-badge ${
                APP_CONFIG.attributes.filter(a => (activeSet?.ratings[a.id] ?? null) !== null).length === APP_CONFIG.attributes.length 
                  ? "completion-badge--complete" : ""
              }">
                ${APP_CONFIG.attributes.filter(a => (activeSet?.ratings[a.id] ?? null) !== null).length} / ${APP_CONFIG.attributes.length}
              </span>
            </div>
            
            ${APP_CONFIG.attributes.map(
              (attr) => html`
                <bab-attribute-rater
                  .attribute="${attr}"
                  .rating="${activeSet?.ratings[attr.id] ?? 0}"
                  ?disabled="${this.locked}"
                  @rating-changed="${this._onRatingChanged}"
                ></bab-attribute-rater>
              `,
            )}
          </div>

          <div class="chart-panel">
            <bab-spider-chart
              .datasets="${this._persistence.state.datasets}"
            ></bab-spider-chart>
          </div>
        </div>

        <bab-share-row
          .locked="${this.locked}"
          .clearPending="${this._clearPending}"
          @download-png="${() => this._downloadImage("png")}"
          @download-svg="${() => this._downloadImage("svg")}"
          @copy-link="${this._copyLink}"
          @clear-requested="${this._clear}"
        ></bab-share-row>
      </div>

      <bab-import-modal
        .open="${this._showImportModal}"
        @close="${() => this._showImportModal = false}"
        @import="${(e: CustomEvent<{url: string}>) => this._importDataset(e.detail.url)}"
      ></bab-import-modal>

      <bab-toast-manager .toasts="${this._toasts.toasts}"></bab-toast-manager>
    `;
  }
}
